'use client'

import type { ReactNode } from 'react'
import * as React from 'react'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, BanknoteArrowDown, CalendarIcon } from 'lucide-react'

import type { DateRange } from '@ui/web/components'
import {
  Button,
  Calendar,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from '@ui/web/components'
import { formatCurrency, formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { PayWithdrawalValues } from './pay-withdrawal-dialog'
import { useSharedTRPC } from '../../context/trpc'
import { PayWithdrawalDialog } from './pay-withdrawal-dialog'
import { RequestWithdrawalDialog } from './request-withdrawal-dialog'
import { RoyaltyRunningBalanceTable } from './table'

export type RoyaltyRunningBalancePageType = 'admin' | 'partner'

export interface RoyaltyRunningBalancePageProps {
  /** When 'partner', only the Request withdrawal button is shown. When 'admin', table shows Pay withdrawal for pending items. */
  type?: RoyaltyRunningBalancePageType
  organizationId?: string
  /** Link or button to go back (e.g. royalty list). Rendered as a child of the back control. */
  backSlot?: ReactNode
  /** Optional URL for the back link when using default back UI. */
  backHref?: string
  className?: string
  /** Optional callback for request withdrawal button */
  onRequestWithdrawal?: () => void
  /** Custom render function for the Report button link. Receives royaltyId and should return a React node. */
  renderReportLink?: (royaltyId: string) => ReactNode
  onPayWithdrawal?: (
    value: PayWithdrawalValues & { withdrawalId: string; defaultAmount: number },
  ) => Promise<void> | void
}

export function RoyaltyRunningBalancePage({
  type = 'partner',
  organizationId,
  backSlot,
  backHref,
  className,
  onRequestWithdrawal,
  renderReportLink,
  onPayWithdrawal,
}: RoyaltyRunningBalancePageProps) {
  const trpc = useSharedTRPC()
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(undefined)
  const [isDatePopoverOpen, setIsDatePopoverOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState('')
  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(10)
  const [requestWithdrawalOpen, setRequestWithdrawalOpen] = React.useState(false)
  const [payWithdrawalEntry, setPayWithdrawalEntry] = React.useState<{
    withdrawalId: string
    amount: number
    currency: string
  } | null>(null)

  const [debouncedSearchValue] = useDebouncedValue(searchValue, { wait: 500 })
  const [debouncedDateRange] = useDebouncedValue(dateRange, { wait: 500 })

  const { data, isPending, isError, error, refetch } = useQuery({
    ...trpc.royalty.getRunningBalance.queryOptions({
      organizationId,
      dateRange:
        debouncedDateRange?.from && debouncedDateRange.to
          ? { from: debouncedDateRange.from, to: debouncedDateRange.to }
          : undefined,
      search: debouncedSearchValue || undefined,
      page,
      pageSize,
    }),
    staleTime: 1000 * 60 * 5,
  })

  React.useEffect(() => {
    setPage(1)
  }, [debouncedSearchValue, debouncedDateRange])

  const totalEarning = data?.totalEarning ?? 0
  const totalWithdrawal = data?.totalWithdrawal ?? 0

  if (isPending) {
    return (
      <div className={className}>
        <div className='srd:flex srd:flex-col srd:gap-6'>
          {/* Back */}
          {(backSlot != null || backHref != null) && (
            <div>
              <Button variant='ghost' className='srd:m-0 srd:p-0 srd:text-base' asChild>
                {backSlot ??
                  (backHref != null ? (
                    <a href={backHref}>
                      <ArrowLeft className='srd:h-4 srd:w-4' />
                      Back
                    </a>
                  ) : null)}
              </Button>
            </div>
          )}

          {/* Current Balance Card */}
          <Card className='srd:shadow-none'>
            <CardHeader className='srd:pb-2'>
              <CardTitle className='srd:text-xs srd:font-medium'>Current Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <Skeleton className='srd:h-8 srd:w-48' />
            </CardContent>
          </Card>

          {/* Balance Detail Section */}
          <Card className='srd:shadow-none'>
            <CardHeader>
              <div className='srd:flex srd:flex-col srd:gap-4'>
                <div className='srd:flex srd:items-center srd:justify-between'>
                  <CardTitle className='srd:text-complementary-black srd:text-lg srd:font-semibold'>
                    Balance Detail
                  </CardTitle>

                  <Button variant='outline' size='default' disabled>
                    <BanknoteArrowDown className='srd:mr-2 srd:size-4' />
                    Request withdrawal
                  </Button>
                </div>

                {/* Filters */}
                <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-4'>
                  <Input
                    placeholder='Search...'
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className='srd:max-w-xs'
                    disabled
                  />
                  <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
                    <PopoverTrigger
                      render={
                        <Button
                          baseButton
                          variant='outline'
                          disabled
                          className={cn(
                            'srd:rounded-lg srd:w-full srd:max-w-xs srd:justify-start srd:font-normal',
                            dateRange ? 'srd:text-complementary-black' : 'srd:text-neutrals-n-50',
                          )}
                        >
                          <CalendarIcon className='srd:mr-2 srd:size-4 srd:text-main-blue-100' />
                          {dateRange?.from ? (
                            dateRange.to ? (
                              <>
                                {formatDate(dateRange.from, { dateStyle: 'medium' })} -{' '}
                                {formatDate(dateRange.to, { dateStyle: 'medium' })}
                              </>
                            ) : (
                              formatDate(dateRange.from, { dateStyle: 'medium' })
                            )
                          ) : (
                            <span>Date range</span>
                          )}
                        </Button>
                      }
                    />
                    <PopoverContent className='srd:w-auto srd:p-0' align='start'>
                      <Calendar
                        mode='range'
                        defaultMonth={dateRange?.from}
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={2}
                      />
                      {dateRange?.from && (
                        <div className='srd:border-t srd:p-3'>
                          <Button
                            variant='outline'
                            size='sm'
                            className='srd:w-full'
                            onClick={() => {
                              setDateRange(undefined)
                              setIsDatePopoverOpen(false)
                            }}
                          >
                            Clear
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardHeader>
            <CardContent className='srd:space-y-4'>
              {/* Summary Cards */}
              <div className='srd:grid srd:gap-4 srd:sm:grid-cols-2'>
                <Card className='srd:shadow-none'>
                  <CardHeader className='srd:pb-2'>
                    <CardTitle className='srd:text-xs srd:font-medium'>Total Earning</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Skeleton className='srd:h-7 srd:w-32' />
                  </CardContent>
                </Card>
                <Card className='srd:shadow-none'>
                  <CardHeader className='srd:pb-2'>
                    <CardTitle className='srd:text-xs srd:font-medium'>Total Withdrawal</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Skeleton className='srd:h-7 srd:w-32' />
                  </CardContent>
                </Card>
              </div>

              {/* Table */}
              <Skeleton className='srd:h-64' />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={className}>
        <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-6'>
          {/* Back */}
          {(backSlot != null || backHref != null) && (
            <div>
              <Button variant='ghost' className='srd:m-0 srd:p-0 srd:text-base' asChild>
                {backSlot ??
                  (backHref != null ? (
                    <a href={backHref}>
                      <ArrowLeft className='srd:h-4 srd:w-4' />
                      Back
                    </a>
                  ) : null)}
              </Button>
            </div>
          )}
          <p className='srd:text-main-red-100'>Failed to load royalty balance: {error.message}</p>
          <Button onClick={async () => await refetch()} variant='outline' size='sm' className='srd:w-fit'>
            Try again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className='srd:flex srd:flex-col srd:gap-6'>
        {/* Back */}
        {(backSlot != null || backHref != null) && (
          <div>
            <Button variant='ghost' className='srd:m-0 srd:p-0 srd:text-base' asChild>
              {backSlot ??
                (backHref != null ? (
                  <a href={backHref}>
                    <ArrowLeft className='srd:h-4 srd:w-4' />
                    Back
                  </a>
                ) : null)}
            </Button>
          </div>
        )}

        {/* Current Balance Card */}
        <Card className='srd:shadow-none'>
          <CardHeader className='srd:pb-2'>
            <CardTitle className='srd:text-xs srd:font-medium'>Current Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <p className='srd:text-complementary-black srd:text-base srd:font-semibold'>
              {formatCurrency(data.currentBalance, {
                currency: data.currency,
                maximumFractionDigits: 2,
              })}
            </p>
          </CardContent>
        </Card>

        {/* Balance Detail Section */}
        <Card className='srd:shadow-none'>
          <CardHeader>
            <div className='srd:flex srd:flex-col srd:gap-4'>
              <div className='srd:flex srd:items-center srd:justify-between'>
                <CardTitle className='srd:text-complementary-black srd:text-lg srd:font-semibold'>
                  Balance Detail
                </CardTitle>

                {type === 'partner' && (
                  <Button
                    variant='outline'
                    size='default'
                    onClick={() => {
                      setRequestWithdrawalOpen(true)
                      onRequestWithdrawal?.()
                    }}
                    disabled
                  >
                    <BanknoteArrowDown className='srd:mr-2 srd:size-4' />
                    Request withdrawal
                  </Button>
                )}
              </div>

              {/* Filters */}
              <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-4'>
                <Input
                  placeholder='Search...'
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className='srd:max-w-xs'
                />
                <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
                  <PopoverTrigger
                    render={
                      <Button
                        baseButton
                        variant='outline'
                        className={cn(
                          'srd:rounded-lg srd:w-full srd:max-w-xs srd:justify-start srd:font-normal',
                          dateRange ? 'srd:text-complementary-black' : 'srd:text-neutrals-n-50',
                        )}
                      >
                        <CalendarIcon className='srd:mr-2 srd:size-4 srd:text-main-blue-100' />
                        {dateRange?.from ? (
                          dateRange.to ? (
                            <>
                              {formatDate(dateRange.from, { dateStyle: 'medium' })} -{' '}
                              {formatDate(dateRange.to, { dateStyle: 'medium' })}
                            </>
                          ) : (
                            formatDate(dateRange.from, { dateStyle: 'medium' })
                          )
                        ) : (
                          <span>Date range</span>
                        )}
                      </Button>
                    }
                  />
                  <PopoverContent className='srd:w-auto srd:p-0' align='start'>
                    <Calendar
                      mode='range'
                      defaultMonth={dateRange?.from}
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={2}
                    />
                    {dateRange?.from && (
                      <div className='srd:border-t srd:p-3'>
                        <Button
                          variant='outline'
                          size='sm'
                          className='srd:w-full'
                          onClick={() => {
                            setDateRange(undefined)
                            setIsDatePopoverOpen(false)
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardHeader>
          <CardContent className='srd:space-y-4'>
            {/* Summary Cards */}
            <div className='srd:grid srd:gap-4 srd:sm:grid-cols-2'>
              <Card className='srd:shadow-none'>
                <CardHeader className='srd:pb-2'>
                  <CardTitle className='srd:text-xs srd:font-medium'>Total Earning</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='srd:text-complementary-black srd:text-xl srd:font-bold'>
                    {formatCurrency(totalEarning, {
                      currency: data.currency,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </CardContent>
              </Card>
              <Card className='srd:shadow-none'>
                <CardHeader className='srd:pb-2'>
                  <CardTitle className='srd:text-xs srd:font-medium'>Total Withdrawal</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='srd:text-complementary-black srd:text-xl srd:font-bold'>
                    {formatCurrency(totalWithdrawal, {
                      currency: data.currency,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            <RoyaltyRunningBalanceTable
              type={type}
              data={data.data}
              isLoading={isPending}
              renderReportLink={renderReportLink}
              pagination={data.pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onPayWithdrawal={
                type === 'admin'
                  ? (entry) =>
                      setPayWithdrawalEntry({
                        withdrawalId: entry.withdrawalId,
                        amount: entry.amount,
                        currency: entry.currency,
                      })
                  : undefined
              }
            />
          </CardContent>
        </Card>

        {type === 'partner' && (
          <RequestWithdrawalDialog
            open={requestWithdrawalOpen}
            onOpenChange={setRequestWithdrawalOpen}
            currentBalance={data.currentBalance}
            currency={data.currency}
            onSuccess={async () => await refetch()}
          />
        )}

        {type === 'admin' && payWithdrawalEntry && onPayWithdrawal && (
          <PayWithdrawalDialog
            currentBalance={data.currentBalance}
            onPayWithdrawal={onPayWithdrawal}
            open={Boolean(payWithdrawalEntry)}
            onOpenChange={(open) => !open && setPayWithdrawalEntry(null)}
            withdrawalId={payWithdrawalEntry.withdrawalId}
            defaultAmount={payWithdrawalEntry.amount}
            currency={payWithdrawalEntry.currency}
            onSuccess={async () => {
              setPayWithdrawalEntry(null)
              await refetch()
            }}
          />
        )}
      </div>
    </div>
  )
}

export type { PayWithdrawalValues }
